import {Auth} from 'aws-amplify'
import {apiCallWithAuthCheck} from '../../auth/core/AuthErrorInterceptor'

/**
 * Fetch wrapper for calls to the OBserver (REACT_APP_API_REST_URL).
 *
 * The OBserver now authenticates requests from the Cognito ID token, exactly like the other
 * backends (JobService, WES). Amplify injects the `Authorization` header automatically for
 * `API.*` calls, but the OBserver is reached with `fetch`, so we attach the token here.
 *
 * Behaviour:
 * - Adds `Authorization: Bearer <idToken>` using the current Cognito session.
 * - Wraps the call in `apiCallWithAuthCheck` so a 401 triggers the shared refresh/retry + re-login
 *   flow. A non-OK response is surfaced as an error carrying `response.status` so the interceptor
 *   can distinguish 401 (re-auth) from 403 (cross-user forbidden — not a session problem).
 */
/**
 * The current Cognito ID token, or undefined if it cannot be obtained.
 *
 * Used to hand the token to external-viewer iframes (via the `getApiUrl` postMessage payload) so
 * their /controller requests to the OBserver can authenticate. The token is baked into the iframe
 * at viewer-open time; a very long-lived viewer could outlive the token's expiry, at which point
 * its controller calls would 401 until the viewer is reopened.
 */
export const getObserverToken = async (): Promise<string | undefined> => {
  try {
    return (await Auth.currentSession()).getIdToken().getJwtToken()
  } catch {
    return undefined
  }
}

export const authedObserverFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  return apiCallWithAuthCheck(async () => {
    const idToken = (await Auth.currentSession()).getIdToken().getJwtToken()

    const response = await fetch(url, {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${idToken}`,
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      const error = new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`
      ) as Error & {response?: {status: number}}
      // Surface the status so AuthErrorInterceptor can react to 401 (and ignore 403).
      error.response = {status: response.status}
      throw error
    }

    return response
  })
}
